import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { xosoService } from "@/services/xosoService";
import useXosoLive, { selectLiveResults } from "../XoSoCompound/useXosoLive";
import { resolveConfigDow, seriesDates } from "./dow";
import { buildStats, orderStations, renderPage, stationFrom } from "./renderBoard";

dayjs.extend(utc);
dayjs.extend(timezone);

const TZ = "Asia/Ho_Chi_Minh";
const REGION = "MT";
const DRAW_AT = "17:15";
const SHOWN = 7;
const STATS_DRAWS = 30;

const resultCache = {};

function todayKey() {
  return dayjs().tz(TZ).format("YYYY-MM-DD");
}

function shiftKey(key, days) {
  return dayjs.tz(key, TZ).subtract(days, "day").format("YYYY-MM-DD");
}

function dayOf(key) {
  return dayjs.tz(key, TZ).day();
}

function dashOf(key) {
  const [year, month, day] = key.split("-");
  return `${day}-${month}-${year}`;
}

function parseClock(value) {
  const match = String(value || "").match(/^(\d{1,2}):(\d{2})/);
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour > 23 || minute > 59) return null;
  return hour * 60 + minute;
}

function nowMinutes() {
  const now = dayjs().tz(TZ);
  return now.hour() * 60 + now.minute();
}

function reachedDraw(scheduledTime) {
  const cutoff = parseClock(scheduledTime) ?? parseClock(DRAW_AT);
  if (cutoff == null) return false;
  return nowMinutes() >= cutoff;
}

function msUntilClock(clock) {
  const minutes = parseClock(clock);
  if (minutes == null) return null;
  const now = dayjs().tz(TZ);
  const target = now.hour(Math.floor(minutes / 60)).minute(minutes % 60).second(0).millisecond(0);
  const diff = target.diff(now);
  return diff > 0 ? diff : null;
}

function toStations(rows) {
  const seen = new Set();
  const stations = [];
  (rows || []).forEach((item) => {
    const station = stationFrom(item);
    if (!station || seen.has(station.code)) return;
    seen.add(station.code);
    stations.push(station);
  });
  return stations;
}

async function mapPool(items, limit, worker) {
  let cursor = 0;
  async function run() {
    while (cursor < items.length) {
      const item = items[cursor];
      cursor += 1;
      await worker(item);
    }
  }
  const width = Math.min(limit, items.length);
  if (!width) return;
  await Promise.all(Array.from({ length: width }, () => run()));
}

export default function useXsmtV4Light(dowInput) {
  const dow = resolveConfigDow(dowInput);
  const [clock, setClock] = useState(0);
  const [packVersion, setPackVersion] = useState(0);
  const today = todayKey();
  const watchesToday = dow === "all" || dow === dayOf(today);
  const live = useXosoLive(REGION, watchesToday);
  const opened = watchesToday && reachedDraw(live.scheduledTime);
  const anchor = opened ? today : shiftKey(today, 1);
  const dates = useMemo(() => seriesDates(anchor, dow, STATS_DRAWS, dayOf), [anchor, dow]);

  useEffect(() => {
    if (!watchesToday || opened) return undefined;
    const wait = msUntilClock(live.scheduledTime || DRAW_AT);
    if (wait == null) return undefined;
    const timer = setTimeout(() => setClock((value) => value + 1), wait + 200);
    return () => clearTimeout(timer);
  }, [watchesToday, opened, live.scheduledTime, clock]);

  useEffect(() => {
    let cancelled = false;
    const missing = dates.filter((date) => !Object.prototype.hasOwnProperty.call(resultCache, date));
    if (!missing.length) {
      setPackVersion((value) => value + 1);
      return undefined;
    }

    (async () => {
      await mapPool(missing, 6, async (date) => {
        resultCache[date] = await xosoService.getXsmt({ date });
      });
      if (!cancelled) setPackVersion((value) => value + 1);
    })();

    return () => {
      cancelled = true;
    };
  }, [dates]);

  return useMemo(() => {
    const pending = dates.some((date) => !Object.prototype.hasOwnProperty.call(resultCache, date));
    if (pending) return { html: "", pending: true, dow };

    const shown = [];
    dates.forEach((date) => {
      if (shown.length >= SHOWN) return;
      const rest = resultCache[date] || [];
      const rows = date === today && opened ? selectLiveResults(rest, live) : rest;
      const stations = orderStations(toStations(rows), dayOf(date));
      if (!stations.length) return;
      shown.push({
        date,
        dow: dayOf(date),
        dash: dashOf(date),
        stations,
      });
    });

    if (!shown.length) return { html: "", pending: true, dow };

    const statsDays = dates
      .filter((date) => Array.isArray(resultCache[date]) && resultCache[date].length)
      .map((date) => ({ rows: resultCache[date] }));

    return {
      html: renderPage({
        dow,
        days: shown,
        stats: buildStats(statsDays),
        today,
        yesterday: shiftKey(today, 1),
        ereyesterday: shiftKey(today, 2),
      }),
      pending: false,
      dow,
    };
  }, [dates, dow, live, opened, packVersion, today]);
}
